import { Dropdown, SelectList, SelectOption } from '@mgmresorts/wcl-core';
import { Input, Typography } from '@mgmresorts/wcl-lab';
import React, { useContext, useState, useCallback, useMemo, useEffect } from 'react';

import StyledContent from '../../../components/ui/Inputs/ManualAddressForm/StyledContent';
import { NavigationLink } from '../../../components/ui/Link';
import { useAddressSearch } from '../../../hooks/useAddressSearch';
import { useContent } from '../../../hooks/useContent';
import { EventAttributes } from '../../../utils/analytics/types';
import { useEvent } from '../../../utils/analytics/useEvent';
import { SignUpFormContext } from '../SignUpFormContext';

const SearchInput = ({
  triggerData,
  onChange,
  predictionError,
}: {
  triggerData: {
    isOpen: boolean;
    toggle: () => void;
    open: () => void;
    close: () => void;
  };
  onChange: (val: string) => void;
  predictionError: boolean;
}) => {
  const trackEvent = useEvent();
  const { isLoading, bindAddress, invalidFields } = useContext(SignUpFormContext);
  const { getContentByPath } = useContent();
  const [analyticsFieldErrors, setAnalyticsFieldErrors] = useState<string[]>([]);

  useEffect(() => {
    if (invalidFields.length > 0) {
      const eventAttributes = {} as EventAttributes;
      if (invalidFields.includes('address') && !analyticsFieldErrors.includes('address')) {
        eventAttributes.errorField = 'Address';
        eventAttributes.errorDisplayed = getContentByPath('shared.fields.address.location.error');
      }

      if (eventAttributes.errorField) {
        trackEvent(
          'identity – mlife - registration input error',
          'mlife registration input error',
          'mlifereginputerror',
          eventAttributes,
        );
      }
    }

    setAnalyticsFieldErrors(invalidFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidFields, invalidFields.length]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      bindAddress.onChange(event);
      if (!triggerData.isOpen) {
        triggerData.open();
      }
      onChange(event.target.value);
    },
    [bindAddress, onChange, triggerData],
  );

  const error = useMemo(
    (): string | undefined =>
      invalidFields.includes('address') ? getContentByPath('shared.fields.address.location.error') : undefined,
    [getContentByPath, invalidFields],
  );

  return (
    <Input
      label={getContentByPath('shared.fields.address.location.title')}
      id='address'
      aria-label={getContentByPath('shared.fields.address.location.label')}
      className={`identity__field ${error ? 'with-error' : ''}`}
      disabled={isLoading}
      hint={predictionError ? ' ' : error}
      error={predictionError || !!error}
      {...bindAddress}
      onChange={handleChange}
    />
  );
};

const PredictionList = (props: {
  onPredictionSelected: (option: SelectOption) => void;
  placePredictions: google.maps.places.AutocompletePrediction[];
}) => {
  const { onPredictionSelected, placePredictions } = props;

  const manualEntryOption = {
    key: 'manual',
    value: 'manual',
    label: 'Enter address manually',
  };

  return (
    <SelectList
      activeOptionIndex={-1}
      id='address-predictions-list'
      onOptionClick={onPredictionSelected}
      options={[
        manualEntryOption,
        ...placePredictions.map((prediction: google.maps.places.AutocompletePrediction) => ({
          key: prediction.place_id,
          value: prediction.place_id,
          label: prediction.description,
        })),
      ]}
      multiple={false}
      value=''
    />
  );
};

const PlacesPredictionsList = ({ onManualEntrySelection }: { onManualEntrySelection: () => void }) => {
  const { setSelectedPlaceId, selectedPlaceId, placePredictions, setAddressValue, autocompleteSessionToken } =
    useContext(SignUpFormContext);
  const [isDropdownOpen, setDropdownOpenState] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getPlaceDetails } = useAddressSearch(autocompleteSessionToken);
  const { getContentByPath } = useContent();

  const getError = useCallback(async () => {
    if (selectedPlaceId) {
      const { address } = await getPlaceDetails(selectedPlaceId);
      if (address?.city && address?.country && address?.street) {
        setError(null);
        return;
      }
      setError(getContentByPath('accountCreation.fields.address.prediction.error'));
      return;
    }
    setError(null);
  }, [getContentByPath, getPlaceDetails, selectedPlaceId]);

  const onPredictionSelected = useCallback(
    (option: SelectOption) => {
      if (option.value === 'manual') {
        onManualEntrySelection();
        setSelectedPlaceId(undefined);
      } else {
        setSelectedPlaceId(option.value);
        if (option.label) {
          setAddressValue(option.label);
        }
      }

      setDropdownOpenState(false);
    },
    [setSelectedPlaceId, setDropdownOpenState, setAddressValue, onManualEntrySelection],
  );

  useEffect(() => {
    getError();
  }, [getError]);

  return (
    <>
      <Dropdown
        aria-label='Address predictions'
        dialogAttachment='top left'
        maxHeight={200}
        offsetTop={0}
        trapFocus={false}
        trigger={triggerData => (
          <SearchInput
            triggerData={triggerData}
            onChange={value => {
              setDropdownOpenState(!!value);
            }}
            predictionError={!!error}
          />
        )}
        triggerAttachment='bottom left'
        open={isDropdownOpen}
        includeA11yCloseButton
        matchMenuMinWidthToTrigger
      >
        <PredictionList placePredictions={placePredictions} onPredictionSelected={onPredictionSelected} />
      </Dropdown>
      {error && (
        <StyledContent>
          <Typography variant='small' className='identity__prediction-error'>
            {error}
            <br />
            <NavigationLink onClick={onManualEntrySelection} path='#'>
              {getContentByPath('accountCreation.fields.address.prediction.insertManually')}
            </NavigationLink>
          </Typography>
        </StyledContent>
      )}
    </>
  );
};

export default PlacesPredictionsList;
